/**
 * makeNestedStatement — builds a synthetic `StatementInfo` that points the
 * SELECT renderer at a sub-range of the buffer (a CTE body, a derived-table
 * subquery body, etc.). The inner renderer doesn't read line/column fields, so
 * those are zeroed; identifiers (`readsFrom` / `writesTo`) are left empty
 * since CTE-scoped resolution lives in the SR-Edit layer (out of VQE-RO scope
 * per `Ralph/SemanticResolverEdit/PRD.md`).
 */
import type { StatementInfo, StatementKind } from '../../../../sql-grammar/model';
import type { CteBinding } from './extractCtes';

export function makeNestedStatement(
  parentStatementId: string,
  binding: CteBinding,
): StatementInfo {
  const kind: StatementKind = binding.bodyKind === 'unknown' ? 'unknown' : binding.bodyKind;
  return {
    id: `${parentStatementId}.cte.${binding.name}`,
    range: {
      startOffset: binding.bodyStart,
      endOffset: binding.bodyEnd,
      startLine: 0,
      startColumn: 0,
      endLine: 0,
      endColumn: 0,
    },
    kind,
    risk: 'read',
    readsFrom: [],
    writesTo: [],
    schemaEffects: [],
  };
}

/**
 * Synthesises a `StatementInfo` covering a subquery body byte range, used by
 * `SubquerySection` to recursively mount `<SelectRenderer>` against the
 * (paren-stripped) inner SELECT. Mirrors `makeNestedStatement` but keys the
 * synthetic id on the body offsets rather than a CTE name (subqueries don't
 * have one).
 */
export function makeNestedSubqueryStatement(
  parentStatementId: string,
  bodyStart: number,
  bodyEnd: number,
): StatementInfo {
  return {
    id: `${parentStatementId}.sub.${bodyStart}-${bodyEnd}`,
    range: {
      startOffset: bodyStart,
      endOffset: bodyEnd,
      startLine: 0,
      startColumn: 0,
      endLine: 0,
      endColumn: 0,
    },
    kind: 'select',
    risk: 'read',
    readsFrom: [],
    writesTo: [],
    schemaEffects: [],
  };
}
